import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchNotizenApi,
  createNotizApi,
  updateNotizApi,
  deleteNotizApi,
  fetchOrdnerApi,
  createOrdnerApi,
  updateOrdnerApi,
  deleteOrdnerApi,
  fetchDateienApi,
  downloadDateiApi,
  createDateiApi,
  deleteDateiApi,
  verschiebeDateiApi,
} from './wissenApi';
import {
  verschluessleJson,
  entschluessleJson,
  verschluessleBytes,
  entschluessleBytes,
} from 'core/features/sicherheit/crypto';
import { holeMasterKey } from 'core/features/sicherheit/vaultHolder';
import { sperren } from 'core/features/sicherheit/sicherheitSlice';
import { logout } from 'core/features/auth/authSlice';

/**
 * Redux-Slice fuer das Wissen-Modul. Haelt die im Browser ENTSCHLUESSELTEN
 * Inhalte (nur im Speicher). Der Master-Key liegt Portal-weit im core-Feature
 * "sicherheit" (vaultHolder). Alle Thunks ver-/entschluesseln an der Grenze zur
 * API. Beim Verlassen des gesicherten Modus (sicherheit/sperren) werden die
 * entschluesselten Inhalte verworfen.
 */

// ── Entschluesselungs-Helfer ────────────────────────────
const entschluessleNotiz = async (mk, dto) => {
  const inhalt = await entschluessleJson(mk, dto.cipherText, dto.iv);
  return {
    uuid: dto.uuid,
    ordnerId: dto.ordnerId ?? null,
    favorit: dto.favorit,
    titel: inhalt.titel || '',
    text: inhalt.text || '',
    tags: inhalt.tags || [],
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
};

const entschluessleOrdner = async (mk, dto) => {
  const meta = await entschluessleJson(mk, dto.cipherName, dto.iv);
  return {
    id: dto.id,
    uuid: dto.uuid,
    parentId: dto.parentId ?? null,
    name: meta.name || '',
    farbe: dto.farbe,
    sortierung: dto.sortierung,
  };
};

const entschluessleDateiMeta = async (mk, dto) => {
  const meta = await entschluessleJson(mk, dto.cipherMeta, dto.metaIv);
  return {
    uuid: dto.uuid,
    ordnerId: dto.ordnerId ?? null,
    notizUuid: dto.notizUuid ?? null,
    name: meta.name,
    typ: meta.typ,
    groesseOriginal: meta.groesse,
    groesse: dto.groesse,
    createdAt: dto.createdAt,
  };
};

// ── Inhalte laden (im gesicherten Modus) ────────────────
export const ladeAlles = createAsyncThunk('wissen/ladeAlles', async () => {
  const [notizDtos, ordnerDtos, dateiDtos] = await Promise.all([
    fetchNotizenApi(),
    fetchOrdnerApi(),
    fetchDateienApi(),
  ]);
  const mk = holeMasterKey();
  return {
    notizen: await Promise.all((notizDtos ?? []).map((d) => entschluessleNotiz(mk, d))),
    ordner: await Promise.all((ordnerDtos ?? []).map((d) => entschluessleOrdner(mk, d))),
    dateien: await Promise.all((dateiDtos ?? []).map((d) => entschluessleDateiMeta(mk, d))),
  };
});

// ── Notizen ─────────────────────────────────────────────
export const speichereNotiz = createAsyncThunk('wissen/speichereNotiz', async (notiz) => {
  const mk = holeMasterKey();
  const blob = await verschluessleJson(mk, { titel: notiz.titel, text: notiz.text, tags: notiz.tags || [] });
  const payload = { cipherText: blob.cipherText, iv: blob.iv, ordnerId: notiz.ordnerId ?? null, favorit: !!notiz.favorit };
  const dto = notiz.uuid ? await updateNotizApi(notiz.uuid, payload) : await createNotizApi(payload);
  return {
    uuid: dto.uuid,
    ordnerId: dto.ordnerId ?? null,
    favorit: dto.favorit,
    titel: notiz.titel,
    text: notiz.text,
    tags: notiz.tags || [],
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
});

export const loescheNotiz = createAsyncThunk('wissen/loescheNotiz', async (uuid) => deleteNotizApi(uuid));

// ── Ordner ──────────────────────────────────────────────
export const speichereOrdner = createAsyncThunk('wissen/speichereOrdner', async (ordner) => {
  const mk = holeMasterKey();
  const blob = await verschluessleJson(mk, { name: ordner.name });
  const payload = {
    cipherName: blob.cipherText,
    iv: blob.iv,
    farbe: ordner.farbe,
    sortierung: ordner.sortierung || 0,
    parentId: ordner.parentId ?? null,
  };
  const dto = ordner.uuid ? await updateOrdnerApi(ordner.uuid, payload) : await createOrdnerApi(payload);
  return {
    id: dto.id,
    uuid: dto.uuid,
    parentId: dto.parentId ?? null,
    name: ordner.name,
    farbe: dto.farbe,
    sortierung: dto.sortierung,
  };
});

export const loescheOrdner = createAsyncThunk('wissen/loescheOrdner', async (uuid) => deleteOrdnerApi(uuid));

// ── Dateien ─────────────────────────────────────────────
export const ladeDateiHoch = createAsyncThunk('wissen/ladeDateiHoch', async ({ file, ordnerId, notizUuid }) => {
  const mk = holeMasterKey();
  const daten = await verschluessleBytes(mk, await file.arrayBuffer());
  const meta = await verschluessleJson(mk, { name: file.name, typ: file.type || 'application/octet-stream', groesse: file.size });
  const payload = {
    ordnerId: ordnerId ?? null,
    notizUuid: notizUuid ?? null,
    cipherMeta: meta.cipherText,
    metaIv: meta.iv,
    datenBase64: daten.datenBase64,
    datenIv: daten.datenIv,
  };
  const dto = await createDateiApi(payload);
  return {
    uuid: dto.uuid,
    ordnerId: dto.ordnerId ?? null,
    notizUuid: dto.notizUuid ?? null,
    name: file.name,
    typ: file.type || 'application/octet-stream',
    groesseOriginal: file.size,
    groesse: dto.groesse,
    createdAt: dto.createdAt,
  };
});

export const ladeDateiHerunter = createAsyncThunk('wissen/ladeDateiHerunter', async (datei) => {
  const voll = await downloadDateiApi(datei.uuid);
  const buffer = await entschluessleBytes(holeMasterKey(), voll.datenBase64, voll.datenIv);
  const url = URL.createObjectURL(new Blob([buffer], { type: datei.typ || 'application/octet-stream' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = datei.name;
  link.click();
  URL.revokeObjectURL(url);
  return datei.uuid;
});

export const loescheDatei = createAsyncThunk('wissen/loescheDatei', async (uuid) => deleteDateiApi(uuid));

export const verschiebeDatei = createAsyncThunk('wissen/verschiebeDatei', async ({ uuid, ordnerId }) => {
  await verschiebeDateiApi(uuid, ordnerId);
  return { uuid, ordnerId: ordnerId ?? null };
});

const initialState = {
  notizen: [],
  ordner: [],
  dateien: [],
  status: 'idle',
  error: null,
};

/** Ersetzt ein Element (per uuid) in einer Liste oder haengt es an. */
const upsert = (liste, element) => {
  const idx = liste.findIndex((e) => e.uuid === element.uuid);
  if (idx >= 0) liste[idx] = element;
  else liste.unshift(element);
};

const nachDatumSortiert = (liste) =>
  [...liste].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

/** Sammelt die id eines Ordners und aller seiner Nachfahren (ueber parentId). */
const sammleTeilbaumIds = (ordner, wurzelId) => {
  const ids = new Set([wurzelId]);
  let gewachsen = true;
  while (gewachsen) {
    gewachsen = false;
    for (const o of ordner) {
      if (o.parentId != null && ids.has(o.parentId) && !ids.has(o.id)) {
        ids.add(o.id);
        gewachsen = true;
      }
    }
  }
  return ids;
};

/** Verwirft alle entschluesselten Inhalte (beim Sperren/Abmelden). */
const leereInhalte = (state) => {
  state.notizen = [];
  state.ordner = [];
  state.dateien = [];
  state.status = 'idle';
  state.error = null;
};

const wissenSlice = createSlice({
  name: 'wissen',
  initialState,
  reducers: {
    fehlerZuruecksetzen(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Verlassen des gesicherten Modus (oder Abmelden): Inhalte verwerfen.
      .addCase(sperren, leereInhalte)
      .addCase(logout, leereInhalte)
      .addCase(ladeAlles.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(ladeAlles.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.notizen = nachDatumSortiert(action.payload.notizen);
        state.ordner = action.payload.ordner;
        state.dateien = action.payload.dateien;
      })
      .addCase(ladeAlles.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(speichereNotiz.fulfilled, (state, action) => {
        upsert(state.notizen, action.payload);
        state.notizen = nachDatumSortiert(state.notizen);
      })
      .addCase(loescheNotiz.fulfilled, (state, action) => {
        state.notizen = state.notizen.filter((n) => n.uuid !== action.payload);
        // Anhaenge der geloeschten Notiz sind serverseitig weg, hier nachziehen.
        state.dateien = state.dateien.filter((d) => d.notizUuid !== action.payload);
      })
      .addCase(speichereOrdner.fulfilled, (state, action) => {
        upsert(state.ordner, action.payload);
      })
      .addCase(loescheOrdner.fulfilled, (state, action) => {
        const geloescht = state.ordner.find((o) => o.uuid === action.payload);
        if (!geloescht) return;
        // Server loescht den ganzen Teilbaum und loest dessen Inhalte; hier nachziehen.
        const teilbaum = sammleTeilbaumIds(state.ordner, geloescht.id);
        state.ordner = state.ordner.filter((o) => !teilbaum.has(o.id));
        state.notizen.forEach((n) => {
          if (teilbaum.has(n.ordnerId)) n.ordnerId = null;
        });
        state.dateien.forEach((d) => {
          if (teilbaum.has(d.ordnerId)) d.ordnerId = null;
        });
      })
      .addCase(ladeDateiHoch.fulfilled, (state, action) => {
        upsert(state.dateien, action.payload);
      })
      .addCase(loescheDatei.fulfilled, (state, action) => {
        state.dateien = state.dateien.filter((d) => d.uuid !== action.payload);
      })
      .addCase(verschiebeDatei.fulfilled, (state, action) => {
        const datei = state.dateien.find((d) => d.uuid === action.payload.uuid);
        if (datei) datei.ordnerId = action.payload.ordnerId;
      });
  },
});

export const { fehlerZuruecksetzen } = wissenSlice.actions;

// ── Selektoren ──────────────────────────────────────────
export const selectNotizen = (state) => state.wissen.notizen;
export const selectOrdner = (state) => state.wissen.ordner;
export const selectDateien = (state) => state.wissen.dateien;
export const selectWissenStatus = (state) => state.wissen.status;

export default wissenSlice.reducer;
